import { useEffect, useRef, useState, type CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';
import { Flex, Typography } from 'antd';
import {
  ArrowLeftIcon,
  ArrowRightIcon,
  CheckCircleIcon,
  CheckIcon,
  LightningIcon,
  XIcon,
} from '@phosphor-icons/react';
import { Button } from '../../../Button';
import { Spinner } from '../../../Spinner';
import { colors } from '../../../../theme-v2';
import { AddonLayout } from '../AddonLayout';
import {
  StyledCard,
  StyledDot,
  StyledEyebrow,
  StyledFlipButton,
  StyledFlipFace,
  StyledFlipInner,
  StyledRatingButton,
} from './QuickRecall.styles';
import { firstUnrevealedIndex } from './helpers';
import { clearLegacyMarks } from './marks';
import type {
  QuickRecallMark,
  QuickRecallNavDirection,
  QuickRecallProps,
  QuickRecallRatings,
} from './types';

const { Text, Title, Paragraph } = Typography;

const eyebrowIconColor = 'rgba(255, 255, 255, 0.7)';

/**
 * QuickRecall — the "Quick recall" Knowledge Hub add-on (backed by the
 * `Flashcards` asset). Each card shows a question (dark front); "Flip to
 * reveal" turns it to the answer (light back), where the viewer self-rates
 * "I knew this" / "New to me". Rating advances to the next card; Prev/Next
 * step through the deck and wrap at both ends. Ratings and flipped cards are
 * reported through `onRatingsChange` / `onRevealedCardsChange` for the app to
 * save. Stateless re: data —
 * the app supplies `cards`; this owns the interaction state and its strings
 * (keys under `experienceV2.quickRecall.*`, registered in the consuming apps'
 * locales).
 */
export const QuickRecall = ({
  cards,
  loading,
  error,
  ratings,
  onRatingsChange,
  revealedCards,
  onRevealedCardsChange,
  className,
  analyticEvents,
}: QuickRecallProps) => {
  const { t } = useTranslation(undefined, { useSuspense: false });
  // Null until the viewer moves or flips: until then the start card is derived.
  const [chosenIndex, setChosenIndex] = useState<number | null>(null);
  const [flipped, setFlipped] = useState(false);
  const [marksState, setMarksState] = useState<QuickRecallRatings | null>(
    null,
  );
  const [revealedState, setRevealedState] = useState<number[] | null>(null);

  // Ratings used to live in localStorage: drop what earlier visits left there.
  useEffect(() => {
    clearLegacyMarks();
  }, []);

  const total = cards.length;
  // This visit's ratings stay on top: anonymous visitors can't save them.
  const marks = { ...ratings, ...marksState };
  // The saved list wins, so a card whose save failed counts as unrevealed again.
  const revealed = revealedCards ?? revealedState ?? [];
  // BE saves revealed cards, not the last viewed card, so resume from the first unrevealed one.
  const index = chosenIndex ?? firstUnrevealedIndex(revealed, total);

  // Latest callbacks in a ref so the reveal effect fires on card changes
  // only — not when a consumer passes a new (inline) analyticEvents object.
  const analyticsRef = useRef(analyticEvents);
  useEffect(() => {
    analyticsRef.current = analyticEvents;
  }, [analyticEvents]);

  // Per-card flip counts for onFlip. Revisiting a card keeps its count.
  const flipCountsRef = useRef<Record<number, number>>({});

  // Reveal: the visible card changed (covers the initial card, Prev/Next and
  // the rating auto-advance). Skipped while there's no deck to show. The
  // last-reported ref dedupes consecutive repeats: a loading flicker
  // (refetch toggling `loading`) re-runs this effect with the same card, and
  // that must not double-report — while returning to a card via navigation
  // (a different index in between) still reveals it again.
  const hasDeck = !loading && !error && total > 0;
  const lastRevealRef = useRef<string | null>(null);
  useEffect(() => {
    if (!hasDeck) return;
    const reveal = `${index}/${total}`;
    if (lastRevealRef.current === reveal) return;
    lastRevealRef.current = reveal;
    // Analytics reporting is a genuine external side effect, not state
    // syncing — the rule's remedy (lift `index` to the parent) would move the
    // deck's interaction state into every consuming app.
    // eslint-disable-next-line react-you-might-not-need-an-effect/no-pass-live-state-to-parent
    analyticsRef.current?.onReveal?.(index, total);
  }, [hasDeck, index, total]);

  const stateMessage = (message: string) => (
    <AddonLayout
      className={className}
      align="center"
      justify="center"
      style={{ minHeight: 160, padding: 24 }}
    >
      {message}
    </AddonLayout>
  );

  if (loading)
    return (
      <AddonLayout
        className={className}
        align="center"
        justify="center"
        style={{ minHeight: 160 }}
      >
        <Spinner />
      </AddonLayout>
    );
  if (error) return stateMessage(t('experienceV2.quickRecall.error'));
  if (total === 0) return stateMessage(t('experienceV2.quickRecall.empty'));

  const card = cards[index];
  // `index` is kept in-bounds by the wrap-around nav below, but
  // noUncheckedIndexedAccess (app tsconfig) can't prove it — guard so the lib
  // source typechecks under both configs.
  if (!card) return null;

  const ratedCount = cards.reduce((n, _c, i) => (marks[i] ? n + 1 : n), 0);
  const currentMark = marks[index];

  // Step through the deck; wraps at both ends and always resets to the question.
  const go = (delta: number) => {
    setChosenIndex((i) => ((i ?? index) + delta + total) % total);
    setFlipped(false);
  };

  // Explicit Prev/Next click — emits navigate with the destination card.
  // Rating advances reuse `go` directly and are deliberately not "navigation".
  const navigate = (direction: QuickRecallNavDirection) => {
    const delta = direction === 'next' ? 1 : -1;
    analyticEvents?.onNavigate?.(direction, (index + delta + total) % total);
    go(delta);
  };

  // Flip the card to its answer, counting flips of this card for onFlip.
  const flip = () => {
    const count = (flipCountsRef.current[index] ?? 0) + 1;
    flipCountsRef.current[index] = count;
    analyticEvents?.onFlip?.(index, count);
    // Pin the card: a new revealed list would move the derived start card.
    setChosenIndex(index);
    setFlipped(true);
    if (!revealed.includes(index)) {
      const next = [...revealed, index];
      setRevealedState(next);
      onRevealedCardsChange?.(next);
    }
  };

  // Rate the current card, persist, then advance to the next card.
  const rate = (value: QuickRecallMark) => {
    const next: QuickRecallRatings = { ...marks, [index]: value };
    setMarksState(next);
    onRatingsChange?.(next);
    analyticEvents?.onRate?.(index, value);
    go(1);
  };

  return (
    <AddonLayout className={className}>
      <Flex vertical flex="1 0 auto" gap={16} style={{ padding: 24 }}>
        <Flex align="center" justify="space-between" gap={8}>
          <Text type="secondary" style={{ fontSize: 12, fontWeight: 600 }}>
            {t('experienceV2.quickRecall.progress', {
              current: index + 1,
              total,
            })}
          </Text>
          <Text
            style={{
              fontSize: 12,
              fontWeight: 600,
              color: colors.accent.active,
            }}
          >
            {t('experienceV2.quickRecall.rated', { rated: ratedCount, total })}
          </Text>
        </Flex>
        <StyledCard>
          <StyledFlipInner $flipped={flipped}>
            {/* Question (front) — hidden from AT once flipped. */}
            <StyledFlipFace aria-hidden={flipped}>
              <StyledEyebrow $onDark>
                <LightningIcon
                  size={14}
                  weight="fill"
                  color={eyebrowIconColor}
                />
                {t('experienceV2.quickRecall.questionLabel', {
                  current: index + 1,
                  total,
                })}
              </StyledEyebrow>
              <Title level={3} style={{ margin: 0, color: '#ffffff' }}>
                {card.front}
              </Title>
              <StyledFlipButton type="button" onClick={flip}>
                {t('experienceV2.quickRecall.flipToReveal')}
              </StyledFlipButton>
            </StyledFlipFace>
            {/* Answer (back) — hidden from AT until flipped. */}
            <StyledFlipFace $back aria-hidden={!flipped}>
              <StyledEyebrow>
                <CheckCircleIcon
                  size={14}
                  weight="fill"
                  color={colors.accent.active}
                />
                {t('experienceV2.quickRecall.answerLabel')}
              </StyledEyebrow>
              <Paragraph
                style={{ margin: 0, fontSize: 18, color: colors.text.primary }}
              >
                {card.back}
              </Paragraph>
              <Flex gap={8} style={{ width: '100%' }}>
                <StyledRatingButton
                  type="button"
                  $variant="known"
                  $active={currentMark === 'known'}
                  aria-pressed={currentMark === 'known'}
                  onClick={() => rate('known')}
                >
                  <CheckIcon size={16} weight="bold" />
                  {t('experienceV2.quickRecall.known')}
                </StyledRatingButton>
                <StyledRatingButton
                  type="button"
                  $variant="review"
                  $active={currentMark === 'review'}
                  aria-pressed={currentMark === 'review'}
                  onClick={() => rate('review')}
                >
                  <XIcon size={16} weight="bold" />
                  {t('experienceV2.quickRecall.review')}
                </StyledRatingButton>
              </Flex>
            </StyledFlipFace>
          </StyledFlipInner>
        </StyledCard>
      </Flex>
      <Flex
        align="center"
        justify="space-between"
        gap={8}
        style={{
          padding: '12px 24px',
          borderTop: `1px solid ${colors.border.subtle}`,
        }}
      >
        <Button
          variant="tertiary"
          size="s"
          leftIcon={<ArrowLeftIcon size={16} />}
          onClick={() => navigate('prev')}
        >
          {t('experienceV2.quickRecall.prev')}
        </Button>
        <Flex align="center" gap={6} aria-hidden style={dotsStyle}>
          {cards.map((_c, i) => (
            <StyledDot key={i} $active={i === index} />
          ))}
        </Flex>
        <Button
          variant="tertiary"
          size="s"
          rightIcon={<ArrowRightIcon size={16} />}
          onClick={() => navigate('next')}
        >
          {t('experienceV2.quickRecall.next')}
        </Button>
      </Flex>
    </AddonLayout>
  );
};

// Let the dot row wrap for larger decks rather than overflow the footer.
const dotsStyle: CSSProperties = { flexWrap: 'wrap', maxWidth: '50%' };
